import React, { useState, useCallback, useEffect, useRef, useMemo } from 'react';
import InputField from 'components/form/input_field/InputField';
import Button from 'components/general/button/Button';
import PhoneInput from 'react-phone-input-2';
import FormInput from 'components/general/form_input/form_input';
import { Size as TooltipSize } from 'components/general/tooltip/tooltip';
import CityInput from 'components/registrationPage/CityInput';
import CitizenshipInput from 'components/registrationPage/CitizenshipInput';
import 'react-phone-input-2/lib/style.css';

const EMAIL_REGEX = /^[-a-z0-9_+.]+@([-a-z0-9]+\.)+[a-z0-9]{2,}$/i;
const DEFAULT_PHONE_COUNTRY = 'pt';
const sanitizePhoneNumber = (value: string): string => value.replace(/[^\d+]/g, '').trim();

interface Question {
  id: number;
  mandatory: boolean;
  question_text: string;
  answer_text: string | null;
}

interface ContactForm {
  email: string;
  fullName: string;
  phone: string;
  city: string;
  country: string;
  countryName: string;
  citizenships: Array<{ value: string }>;
  googlePlaceId: string;
  latitude: string;
  longitude: string;
}

interface ContactStepProps {
  jobId: string;
  profile: Partial<ContactForm>;
  citizenships?: string[];
  onNext: (contactForm: ContactForm, needsWorkPermit: boolean, questions?: Question[]) => void;
  onPrevious?: () => void;
}

const ContactStep: React.FC<ContactStepProps> = ({
  jobId,
  profile,
  citizenships,
  onNext,
  onPrevious,
}) => {
  const styles = require('./Step.module.scss');
  const authenticityToken = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || '';

  const initializeContactForm = useCallback((): ContactForm => ({
    email: profile.email || '',
    fullName: profile.fullName || '',
    phone: profile.phone || '',
    city: profile.city || '',
    country: profile.country || '',
    countryName: profile.countryName || '',
    citizenships: profile.citizenships || [],
    googlePlaceId: profile.googlePlaceId || '',
    latitude: profile.latitude || '',
    longitude: profile.longitude || '',
  }), [profile]);

  const [contactForm, setContactForm] = useState<ContactForm>(initializeContactForm);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const fullNameInputRef = useRef<HTMLInputElement>(null);

  const handleDropdownPosition = useCallback(() => {
    const phoneInput = document.querySelector('.react-tel-input');
    if (!phoneInput) return;

    const input = phoneInput.querySelector('input');
    const dropdown = phoneInput.querySelector('.country-list');
    
    if (!input || !dropdown) return;

    const inputRect = input.getBoundingClientRect();
    const dropdownHeight = 300;
    const spaceBelow = window.innerHeight - inputRect.bottom;
    const spaceAbove = inputRect.top;

    dropdown.classList.remove('top', 'bottom');
    
    if (spaceBelow < dropdownHeight && spaceAbove > dropdownHeight) {
      dropdown.classList.add('top');
    } else {
      dropdown.classList.add('bottom');
    }
  }, []);

  useEffect(() => {
    fullNameInputRef.current?.focus();
    
    document.addEventListener('click', handleDropdownPosition);
    window.addEventListener('resize', handleDropdownPosition);
    
    return () => {
      document.removeEventListener('click', handleDropdownPosition);
      window.removeEventListener('resize', handleDropdownPosition);
    };
  }, [handleDropdownPosition]);

  const isEmailValid = EMAIL_REGEX.test(contactForm.email.trim());
  const disableNext = useMemo(() => {
    return (
      isSubmitting ||
      !contactForm.fullName.trim() ||
      !isEmailValid ||
      !contactForm.country.trim() ||
      contactForm.citizenships.length === 0
    );
  }, [isSubmitting, contactForm.fullName, contactForm.email, isEmailValid, contactForm.country, contactForm.citizenships]);

  const handleFormChange = useCallback((event: any, input: string) => {
    if (input === 'location') {
      const addressArray = event.formatted_address.split(', ');
      const city = addressArray[0];
      const countryComponent = event.address_components.find(
        (component: any) => component.types.includes('country')
      );
      const countryShort = countryComponent?.short_name || '';
      const countryLong = countryComponent?.long_name || '';
      
      setContactForm(prev => ({
        ...prev,
        city,
        country: countryShort,
        countryName: countryLong,
        googlePlaceId: event.place_id,
        latitude: event.geometry?.location?.lat()?.toString() || '',
        longitude: event.geometry?.location?.lng()?.toString() || '',
      }));
    } else {
      setContactForm(prev => ({ ...prev, [input]: event }));
    }
  }, []);

  const handleNext = useCallback(async () => {
    if (disableNext) return;

    setIsSubmitting(true);

    const response = await fetch(
      window.Routes.profile_job_ad_quick_applications_url(jobId),
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': authenticityToken,
        },
        body: JSON.stringify({
          profile: {
            unconfirmed_email: contactForm.email,
            full_name: contactForm.fullName,
            phone_number: contactForm.phone,
            city: contactForm.city,
            country_code: contactForm.country,
            citizenships: contactForm.citizenships.map((c: { value: string }) => c.value),
            google_place_id: contactForm.googlePlaceId,
            latitude: contactForm.latitude,
            longitude: contactForm.longitude,
          },
        }),
      }
    );

    const data = await response.json();

    setIsSubmitting(false);

    if (response.ok && data.success) {
      onNext(contactForm, data.needs_work_permit, data.questions);
    }
  }, [disableNext, jobId, authenticityToken, contactForm, onNext]);

  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if ((e.key === 'Enter' || e.key === 'Go') && !disableNext) {
      e.preventDefault();
      handleNext();
    }
  }, [disableNext, handleNext]);

  const phoneInputProps = useMemo(() => ({
    name: 'profile[phone_number]',
    required: true,
    autoComplete: 'tel',
    id: 'phone_number',
    onKeyDown: handleKeyDown,
  }), [handleKeyDown]);
  
  return (
    <>
      <div className={styles.form}>
        <InputField
          id="full_name"
          name="profile[full_name]"
          label="Full name"
          placeholder="First and last name"
          type="text"
          value={contactForm.fullName}
          onChange={(e) => setContactForm(prev => ({ ...prev, fullName: e.target.value }))}
          onKeyDown={handleKeyDown}
          required
          inputRef={fullNameInputRef}
        />
        
        <FormInput
          name="phoneNumber"
          labelClass={'formInput'}
          label={
            <>
              <span className={styles.phoneLabel}>Phone number</span>{' '}
              <span className={styles.optional}>(optional)</span>
            </>
          }
          tooltip={{
            content: 'Including your number increases your chances of being contacted.',
            size: TooltipSize.Medium,
            iconColor: 'tuftsBlueBase',
          }}
        >
          <PhoneInput
            country={DEFAULT_PHONE_COUNTRY}
            value={contactForm.phone}
            onChange={(value) => setContactForm(prev => ({ ...prev, phone: sanitizePhoneNumber(value) }))}
            inputClass={styles.phoneInput}
            inputProps={phoneInputProps}
            enableSearch
            countryCodeEditable={false}
            disableSearchIcon={true}
            inputStyle={{
              width: '100%',
            }}
            searchStyle={{
              width: '90%',
            }}
          />
        </FormInput>
            
        <CityInput
          error={''}
          class={styles.actionButton}
          handleFormChange={handleFormChange}
          city={contactForm.city}
          country={contactForm.country}
          countryName={contactForm.countryName}
          label={'Current country location'}
          labelClass={styles.inputsText}
        />

        <CitizenshipInput
          error={""}
          options={citizenships}
          citizenships={contactForm.citizenships}
          handleFormChange={handleFormChange}
          label={'Citizenship'}
          labelClass={styles.inputsText}
        />
      </div>

      <div className={styles.footer}>
        {onPrevious && (
          <Button
            onClick={onPrevious}
            type="Secondary"
            size="M"
            iconLeftName="chevronLeft"
            className={styles.actionButton}
          >
            Previous
          </Button>
        )}

        <Button
          onClick={handleNext}
          disabled={disableNext}
          size="M"
          iconRightName="chevronRight"
          className={styles.actionButton}
        >
          Next
        </Button>
      </div>
    </>
  );
};

export default ContactStep;
